import { v4 as uuid } from 'uuid';
import { isReactive, isRef, onMounted, toRaw } from 'vue';
import { i18n } from '@/common/locale';
import localeEn from '@/common/locale/en.json';
import {
  DEFAULT_PRODUCT_THEME,
  ProductTheme,
  TIME_SHORT_UNITS,
  TIMEZONE_ITEMS,
} from '@/common/utils/define';
import type { FormatSize, TimezoneName, TimezoneNameForApiParam } from '@/common/utils/types';
import { ALL_ICON_SET } from '@/common/define/icon.define';
import { IconType } from '@/types/icon.types';
import packageInfo from '../../../package.json';
import { useMyInfoStore } from '@/config/stores/my-info';
import dayjs from 'dayjs';

export * from './string.utils';
export * from './time.utils';

const getVersion = (type: 'product' | 'front' = 'product') => {
  const [major, minor, patch] = packageInfo.version.split('.');
  return type === 'product' ? `v${major}.${minor}` : `${major}.${minor}.${patch}`;
};

export {
  NON_PRINTABLE_ASCII_REGEX,
  replaceTextNodes,
  getCSSVariable,
  getMeasuredTextWidth,
  scrollToBottom,
  setScrollTopForFullWindow,
} from './browser/dom';

export { downloadByUrl, fileDownload } from './browser/file';

export {
  convertCamelToCapital,
  convertSentenceCase,
  convertCamelToKebab,
} from './core/string/format';

export {
  compareArrayFieldValues,
  areArraysEqualUnorderedNoneDuplicated,
  isAllDisplayNameCheck,
  sortObjectByKey,
} from './core/array/manipulation';

export {
  numberWithComma,
  formatNumberToLocale,
  getRoundedNumberWithComma,
  roundToDigitNumber,
  roundToDigit,
  roundToDigitValue,
  formatNumWithCommasAndDecimal,
  formatNumWithCommasAndDecimalStr,
  formatTruncatedNumberWithComma,
  convertUnitValue,
  reverseFormatBytes,
} from './core/number/format';

export {
  convertBytesToUnits,
  convertMbToGb,
  convertNumberToUnits,
  convertBpsToUnit,
  convertMsToSec,
  convertSecToMs,
  convertMsToDynamicTime,
} from './core/number/conversion';

export { getRequireParam, removeNullValues } from './core/object/manipulation';

export {
  convertTimeToRangeTime,
  convertTimeToSec,
  convertToDynamicTime,
} from './core/time/conversion';

export { formatTimeByInterval } from './core/time/format';

export {
  getDataArrangeIntervalByStartEndTime,
  getDataArrangeIntervalByTimeRange,
  getInterpolateTypeByInterval,
  getTimeBy,
  getTimeIntervalByTimePeriod,
} from './core/time/interval';

export {
  confirmMsg,
  promiseConfirmMsg,
  showSuccessMsg,
  showErrorMsg,
  showWarningMsg,
  showInfoMsg,
  showMsg,
  getErrorMsgKeyOfPromiseReject,
  showErrorMsgOfPromiseReject,
  standardTimeToUtcZeroTime,
  utcZeroTimeToStandardTime,
  getElapsedTime,
} from './browser';

export { getRgbaChangedOpacity, extractRgbaOpacity } from './chart/color';

export {
  compareMysqlVersion,
  isMysqlSessionTempAvailable,
  isMysqlCpuTimeAvailable,
} from './database/mysql';

export { formatSqlText } from './database/sql';

export { truthyNumber, isFalsyPhv } from './validation/common';

// utcOffset 값을 가져온다 (서머타임 반영)
const getUtcOffset = (): number => {
  const { timezone } = useMyInfoStore();

  if (!timezone) {
    return 0;
  }

  return dayjs().tz(timezone).utcOffset();
};

const getUtcOffsetByTimezone = (timezone?: TimezoneName): number => {
  if (!timezone || timezone === 'NONE') return 0;
  return TIMEZONE_ITEMS.find((v) => v.value === timezone)?.utcOffsetMin || 0;
};

/**
 * Creates a short, deterministic hash string from a given input.
 *
 * Designed for non-cryptographic use cases such as
 * generating stable storage keys for dynamic configurations.
 *
 * @param {string} str
 * @returns {string}
 */
export const getHashString = (str: string): string => {
  let hash = 0;

  for (let i = 0; i < str.length; i++) {
    hash = Math.imul(hash, 31) + str.charCodeAt(i);
  }

  return Math.abs(hash).toString(36);
};

const replaceInstanceObjectType = (type: string): string => (type === 'i' ? 'Index' : 'Table');

type Cb = (comparison: number) => number;
interface Fn {
  (max: number, comparison: number): number;
  (max: number): Cb;
  (max: number, comparison?: number): number | Cb;
}
const getRatio = function (max: number, comparison?: number): number | Cb {
  if (comparison === undefined) {
    return (n) => getRatio(max, n);
  }
  if (comparison === 0 || max === 0) {
    return 0;
  }
  return comparison / max; // 소수점 처리
} as Fn;

const getTimezoneItemsByLocale = () =>
  TIMEZONE_ITEMS.map((timezoneItem) => {
    const key = timezoneItem.utcTimeOffset
      .replace('+', 'PLUS_')
      .replace('-', 'MINUS_')
      .replace(':', '_');

    return {
      ...timezoneItem,
      name: i18n.global.t(`DESC.TIMEZONE.${key}`),
    };
  });

const getTimeZoneByLocale = (timezone: string) => {
  if (!timezone) {
    return '';
  }

  const timezoneInfo = TIMEZONE_ITEMS.find((v) => timezone === v.value);
  const key = timezoneInfo?.utcTimeOffset
    .replace('+', 'PLUS_')
    .replace('-', 'MINUS_')
    .replace(':', '_');
  return i18n.global.t(`DESC.TIMEZONE.${key}`);
};

const getAPIErrorStatusText = (e, options?: { useCancelError?: boolean }): string => {
  // e.ignoreRequest -> API isPolling reject 시에는 무시
  if (!Object.keys(e ?? {}).length || e.ignoreRequest) {
    return '';
  }

  const { useCancelError = true } = options ?? {};

  // 응답 객체가 있는 경우 응답 코드로 처리함
  if (e?.response) {
    const { status, statusText } = e.response;

    if (statusText) {
      return statusText;
    }
    if (status === 403) {
      return localeEn.NOTI.API.NOT_PERMISSION;
    }
    if (status === 400) {
      return localeEn.NOTI.API.INVALID_REQUEST;
    }
    if (status === 405) {
      return localeEn.NOTI.API.NOT_ALLOW_METHOD;
    }
    if (status === 500) {
      return localeEn.NOTI.API.SERVER_ERROR;
    }
    if (status === 503) {
      return localeEn.NOTI.API.NOT_AVAILABLE_SERVICE;
    }

    return localeEn.NOTI.API.DEFAULT_ERROR;
  }

  if (e?.request) {
    if (e.code === 'ECONNABORTED') {
      return localeEn.NOTI.API.TIMEOUT_ERROR;
    }
    if (e.code === 'ERR_CANCELED') {
      return useCancelError ? localeEn.NOTI.API.CANCEL_ERROR : '';
    }

    if (e.code === 'ERR_NETWORK') {
      return localeEn.NOTI.API.ERR_NETWORK;
    }

    return localeEn.NOTI.API.NOT_RECEIVE;
  }

  return localeEn.NOTI.API.REQUEST_ERROR;
};

/**
 * 숫자를 바이트 단위로 변환
 */
const formatBytes = (bytes: number, startUnit: FormatSize = 'Bytes', decimals = 2) => {
  let bytesNum = bytes;
  if (bytesNum === 0) return '0';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];

  let i = sizes.indexOf(startUnit);
  if (i === -1) {
    console.log('startUnit is not valid');
    i = 0;
  }

  while (bytesNum >= k && i < sizes.length - 1) {
    bytesNum /= k;
    i++;
  }

  return `${bytesNum.toFixed(dm)} ${sizes[i]}`;
};

const sortFnFormatBytes = (a: string, b: string) => {
  const sizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];

  const [aValue, aUnit] = (a ?? '').split(' ');
  const [bValue, bUnit] = (b ?? '').split(' ');

  const aUnitIndex = sizes.indexOf(aUnit === 'byte' ? 'Bytes' : aUnit);
  const bUnitIndex = sizes.indexOf(bUnit === 'byte' ? 'Bytes' : bUnit);

  if (aUnitIndex === -1) {
    return -1;
  }

  if (bUnitIndex === -1) {
    return 1;
  }

  if (aUnitIndex === bUnitIndex) {
    return Number(aValue) - Number(bValue);
  }

  return aUnitIndex - bUnitIndex;
};

const waitTime = async (delayTimeMs: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const timeoutId = setTimeout(resolve, delayTimeMs);

    signal?.addEventListener('abort', () => {
      clearTimeout(timeoutId);
      reject(new Error('Operation cancelled'));
    });
  });

/**
 * 현재 테마를 일회성으로 읽습니다.
 * 테마 변경에 반응해야 하는 경우 {@link useThemeDataset}을 사용하세요.
 */
const getCurrentTheme = (): ProductTheme => {
  const datasetTheme = document.documentElement.dataset.theme ?? '';
  const map = {
    light: 'light',
    dark: 'dark',
  } satisfies Record<string, ProductTheme>;
  return map[datasetTheme] ?? DEFAULT_PRODUCT_THEME;
};
const usePrismTheme = () => {
  onMounted(() => {
    if (getCurrentTheme() === 'light') {
      import('prismjs/themes/prism.css');
    }
  });
};

const TIMEZONE_EXCEPTION_MAP = {
  'America/Argentina/Buenos_Aires': 'America_Argentina_BuenosAires',
  'America/Glace_Bay': 'America_GlaceBay',
  'America/St_Johns': 'America_StJohns',
} as const;

type TimezoneException = keyof typeof TIMEZONE_EXCEPTION_MAP;
const INVALID_TIMEZONE_FOR_API = new Set<TimezoneName>([
  'NONE',
  'UTC',
  'Pacific/Auckland',
  'Australia/Canberra',
  ...(Object.keys(TIMEZONE_EXCEPTION_MAP) as TimezoneName[]),
]);

const getTimezoneForApiParam = (): TimezoneNameForApiParam => {
  const { timezone } = useMyInfoStore();

  if (timezone && timezone in TIMEZONE_EXCEPTION_MAP) {
    return TIMEZONE_EXCEPTION_MAP[timezone as TimezoneException];
  }

  const safeTimezone = (
    TIMEZONE_ITEMS.some((item) => item.value === timezone) &&
    !INVALID_TIMEZONE_FOR_API.has(timezone as TimezoneName)
      ? timezone
      : 'Asia/Seoul'
  ) as TimezoneName;

  return safeTimezone.replace('/', '_') as TimezoneNameForApiParam;
};

const getIntervalUnitOrder = (unit: string): number => {
  return TIME_SHORT_UNITS.indexOf(unit);
};

const getIntervalNumericValue = (str: string): number => {
  return +str.replace(/[^0-9]/g, '');
};

const getUnitFromInterval = (str: string): string => {
  if (str.endsWith('ms')) {
    return 'ms';
  }
  return str[str.length - 1];
};

const compareInterval = (aInterval: string, bInterval: string) => {
  const aUnitOrder = getIntervalUnitOrder(getUnitFromInterval(aInterval));
  const bUnitOrder = getIntervalUnitOrder(getUnitFromInterval(bInterval));
  const aVal = getIntervalNumericValue(aInterval);
  const bVal = getIntervalNumericValue(bInterval);

  // 같은 경우
  if (aInterval === bInterval) {
    return 0;
  }
  // 단위 우선 순위
  if (aUnitOrder > bUnitOrder) {
    return 1;
  }
  if (aUnitOrder < bUnitOrder) {
    return -1;
  }
  // 값 우선 순위
  if (aVal > bVal) {
    return 1;
  }
  return -1;
};

const isIncludeTextWithoutWhitespace = (base: string, target: string): boolean => {
  const baseText = base.toUpperCase().replace(/\s+/g, '');
  const targetText = target.toUpperCase().replace(/\s+/g, '');

  return baseText.includes(targetText);
};

const generateUUID = () => {
  return uuid();
};

const adjustSummaryRange = (
  minMs: number,
  maxMs?: number,
): { minMsElapse: number; maxMsElapse?: number } => {
  // 0~99 ms 값이 1ms 로 보정되므로 100 미만일 경우 절삭이 아니라 그대로 요청하도록 반환
  let minMsElapse = minMs < 100 ? minMs : Math.floor(minMs / 100) * 100;

  // 1 미만일 경우 0인 값이 보여야하여 0으로 반환
  if (minMs < 1) {
    minMsElapse = 0;
  }

  const maxMsElapse = !maxMs ? maxMs : Math.floor(maxMs / 100) * 100 + 99;
  return {
    minMsElapse,
    maxMsElapse,
  };
};

/**
 * 정상 갯수와 비정상 갯수를 받아서 비율을 반환하는 함수
 * 값이 정수값이 0이하의 경우 올림 처리 그 이외에는 내림 처리
 * @param normal 정상 갯수(기준 갯수)
 * @param error  비정상 갯수(나머지 갯수)
 * @param digit 소수점 자리수 (default 2자리수)
 */
const getPercentValue = (normal: number = 0, error: number = 0, digit: number = 2) => {
  const percentValue = (normal / (normal + error)) * 100;
  const integer = Math.trunc(percentValue);
  const exponentsNum = 10 ** digit;
  return (
    (integer <= 0
      ? Math.ceil(percentValue * exponentsNum)
      : Math.floor(percentValue * exponentsNum)) / exponentsNum || 0
  );
};

const toRawDeep = <T>(obj: T): T => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (isRef(obj) || isReactive(obj)) {
    return toRawDeep(toRaw(obj));
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => toRawDeep(item)) as T;
  }

  const result = {} as T;

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      result[key] = toRawDeep(obj[key]);
    }
  }

  return result;
};

const isValidIconType = (icon: string): icon is IconType => {
  return ALL_ICON_SET.has(icon);
};

export {
  getVersion,
  replaceInstanceObjectType,
  getUtcOffset,
  getUtcOffsetByTimezone,
  getRatio,
  getTimezoneItemsByLocale,
  getTimeZoneByLocale,
  getAPIErrorStatusText,
  formatBytes,
  sortFnFormatBytes,
  waitTime,
  getCurrentTheme,
  usePrismTheme,
  getTimezoneForApiParam,
  compareInterval,
  isIncludeTextWithoutWhitespace,
  generateUUID,
  adjustSummaryRange,
  getPercentValue,
  toRawDeep,
  isValidIconType,
};
